import { differenceInMinutes } from 'date-fns';
import { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { UserPrivate } from '../../../../../../../common/iam/users/types';
import { Contribution, ContributionType } from '../../../../../../../common/model/input/contributions/types';
import { sendContribution } from '../../../../../../untidy/redux/modules/contribution';
import {
  ContributionSelectionType,
  isContributionSelectionType,
  updateModal,
} from '../../../../../../untidy/redux/modules/modal';
import { StateTree } from '../../../../../../untidy/redux/reducers';
import {
  getOpenModal,
  getRequestError,
  isRequestOngoing,
  loggedInUser,
} from '../../../../../../untidy/redux/selectors';
import { aiName } from '../../../../../../untidy/texts';
import { NiceButton } from '../../../../../generic/components/buttons/nice-button';
import { getBouLinkStyle } from '../../../../../generic/content/texts/bou-link';
import { getTextStyle } from '../../../../../generic/content/texts/bou-text/text-style';
import { BouColor } from '../../../../../generic/global/bou-colors';
import { defaultMobileBreakpointDEPRECATED, themeDEPRECATED } from '../../../../../generic/global/styles';
import { BouSpacingLevel, fluidSpacing } from '../../../../../generic/layout/bou-spacing';
import { Modal } from '../../../../../generic/modals/redux-modal';
import { assertNever } from '../../../../../generic/utilities/utils';
import { ContributionModalParagraph, ContributionModalTitle, FormRow } from './common';
import { CompanyMetadataForm } from './company-metadata-form';
import { FormButton } from './form-button';
import { InvalidCompanyProductsForm } from './invalid-company-products-form';
import { InvalidProductForm } from './invalid-product-form';
import { MissingCompanyProductsForm } from './missing-company-products-form';
import { NewCompanyForm } from './new-company-form';
import { NewProductForm } from './new-product-form';
import { OpenCommentForm } from './open-comment-form';
import { ProductKnowledgeForm } from './product-knowledge-form';
import { WeirdImpactForm } from './weird-impact-form';
import { url as thanksImage } from './images/leena-leena-1-with-text.svg';

export const CONTRIBUTE_MODAL_NAME = 'CONTRIBUTE';

const CancelText = styled.div`
  margin-top: ${themeDEPRECATED.rhythm(5)};
  ${themeDEPRECATED.fontSize(1)};
  color: ${themeDEPRECATED.colors.grayWarm()};
  cursor: pointer;

  &:hover {
    color: ${themeDEPRECATED.colors.black()};
  }
`;

const FullWidthFormButton = styled(FormButton)`
  width: 100%;
  text-align: left;
  padding-left: ${themeDEPRECATED.rhythm(4.5)};
  padding-right: ${themeDEPRECATED.rhythm(4.5)};
  @media (max-width: ${defaultMobileBreakpointDEPRECATED}px) {
    padding: ${themeDEPRECATED.rhythm(4)};
  }
`;

const FullWidthFormButtonWithCenter = styled(FullWidthFormButton)`
  text-align: center;
`;

const ErrorMessage = styled.div`
  margin: ${themeDEPRECATED.rhythm(4)} 0;
  text-align: center;
  width: 100%;
  padding: 0 2rem;
  ${themeDEPRECATED.fontMedium};
  line-height: 1.88;
  letter-spacing: -0.2px;
  color: ${themeDEPRECATED.colors.redError()};
`;

const BackLink = styled.a`
  display: block;
  ${getTextStyle({ size: 'xxs' })}
  ${getBouLinkStyle()}
  ${fluidSpacing('margin-bottom', BouSpacingLevel.MICRO)}
`;

const ThanksRoot = styled.div`
  display: flex;
`;

const ThanksImage = styled.img`
  flex-shrink: 0;
  display: block;
  width: 117px;
  height: 163px;
  flex-basis: 117px;
`;

const ThanksText = styled.div``;

export const HowToHelpOption = styled.div`
  cursor: pointer;

  & + & {
    margin-top: ${themeDEPRECATED.rhythm(3)};
  }

  &:hover {
    color: ${BouColor.TOOL_HOVER_DARK_GRAY};
  }
`;

interface DispatchProps {
  submitContribution: (options: { target?: string; data: Contribution }) => void;
  selectContributionModalType: (type: ContributionType | ContributionSelectionType) => void;
}

interface StateProps {
  currentlyOpenModal: ReturnType<typeof getOpenModal>;
  isSubmitting: boolean;
  serverError?: string;
  isOpen: boolean;
  user?: UserPrivate;
}

type Props = StateProps & DispatchProps;

class ContributeModalPlain extends Component<Props> {
  public componentDidMount() {
    if (this.props.isOpen && !this.props.user && this.props.currentlyOpenModal?.options.type !== 'OPEN_COMMENT') {
      console.warn('user not signed in');
      // this.props.openSignupModal();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      !this.props.isOpen &&
      nextProps.isOpen &&
      !nextProps.user &&
      nextProps.currentlyOpenModal?.options.type !== 'OPEN_COMMENT'
    ) {
      console.warn('user not signed in');
      // nextProps.openSignupModal();
    }
  }

  private getBackLink(type: ContributionType, target?: string) {
    const { selectContributionModalType } = this.props;
    switch (type) {
      case 'PRODUCT_KNOWLEDGE':
      case 'PRODUCT_WEIRD_IMPACT':
      case 'PRODUCT_INVALID':
        return (
          <BackLink
            onClick={() => {
              selectContributionModalType('product');
            }}
          >
            ← Back
          </BackLink>
        );
      case 'COMPANY_INVALID_METADATA':
      case 'COMPANY_INVALID_PRODUCTS':
      case 'COMPANY_MISSING_PRODUCTS':
      case 'COMPANY_WEIRD_IMPACT':
        return (
          <BackLink
            onClick={() => {
              selectContributionModalType('company');
            }}
          >
            ← Back
          </BackLink>
        );
      case 'NEW_PRODUCT':
        if (target) {
          // Only show the back link if we've come from a product page
          return (
            <BackLink
              onClick={() => {
                selectContributionModalType('product');
              }}
            >
              ← Back
            </BackLink>
          );
        }
        return null;
      default:
        return null;
    }
  }

  private getForm(type: ContributionType) {
    const { currentlyOpenModal, isSubmitting, submitContribution } = this.props;
    const options = currentlyOpenModal && currentlyOpenModal.options;

    switch (type) {
      case 'NEW_PRODUCT':
        return <NewProductForm isSubmitting={isSubmitting} submitContribution={submitContribution} />;
      case 'NEW_COMPANY':
        return <NewCompanyForm isSubmitting={isSubmitting} submitContribution={submitContribution} />;
      case 'OPEN_COMMENT':
        return <OpenCommentForm isSubmitting={isSubmitting} submitContribution={submitContribution} />;
      case 'COMPANY_INVALID_METADATA':
        if (!options) {
          console.error('Missing options for company metadata form');
          return null;
        }
        return (
          <CompanyMetadataForm options={options} isSubmitting={isSubmitting} submitContribution={submitContribution} />
        );
      case 'COMPANY_INVALID_PRODUCTS':
        if (!options) {
          console.error('Missing options for invalid company products form');
          return null;
        }
        return (
          <InvalidCompanyProductsForm
            options={options}
            isSubmitting={isSubmitting}
            submitContribution={submitContribution}
          />
        );
      case 'COMPANY_MISSING_PRODUCTS':
        if (!options) {
          console.error('Missing options for missing company products form');
          return null;
        }
        return (
          <MissingCompanyProductsForm
            options={options}
            isSubmitting={isSubmitting}
            submitContribution={submitContribution}
          />
        );
      case 'PRODUCT_KNOWLEDGE':
        if (!options) {
          console.error('Missing options for product knowledge form');
          return null;
        }
        return (
          <ProductKnowledgeForm options={options} isSubmitting={isSubmitting} submitContribution={submitContribution} />
        );
      case 'COMPANY_WEIRD_IMPACT':
        if (!options) {
          console.error('Missing options for weird impact form');
          return null;
        }
        return (
          <WeirdImpactForm
            targetType="company"
            options={options}
            isSubmitting={isSubmitting}
            submitContribution={submitContribution}
          />
        );
      case 'PRODUCT_WEIRD_IMPACT':
        if (!options) {
          console.error('Missing options for weird impact form');
          return null;
        }
        return (
          <WeirdImpactForm
            targetType="product"
            options={options}
            isSubmitting={isSubmitting}
            submitContribution={submitContribution}
          />
        );
      case 'PRODUCT_INVALID':
        if (!options) {
          console.error('Missing options for invalid product form');
          return null;
        }
        return (
          <InvalidProductForm options={options} isSubmitting={isSubmitting} submitContribution={submitContribution} />
        );
    }

    assertNever(type);
    return null;
  }

  private getCompanyContributionTypeSelection(closeModal: () => void, _targetName?: string) {
    return (
      <>
        <ContributionModalTitle>What issue would you like to improve in this profile?</ContributionModalTitle>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('COMPANY_INVALID_METADATA');
          }}
        >
          › The revenue or employee figures look strange
        </HowToHelpOption>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('COMPANY_MISSING_PRODUCTS');
          }}
        >
          › A product is missing for this company
        </HowToHelpOption>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('COMPANY_INVALID_PRODUCTS');
          }}
        >
          › There's a wrong product for this company
        </HowToHelpOption>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('COMPANY_WEIRD_IMPACT');
          }}
        >
          › I want to comment on the impact profile
        </HowToHelpOption>
        <CancelText onClick={closeModal}>Nevermind, I'll continue exploring</CancelText>
      </>
    );
  }

  private getProductContributionTypeSelection(closeModal: () => void, _targetName?: string) {
    return (
      <>
        <ContributionModalTitle>What issue would you like to improve in this profile?</ContributionModalTitle>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('PRODUCT_INVALID');
          }}
        >
          › This product label does not make sense
        </HowToHelpOption>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('PRODUCT_KNOWLEDGE');
          }}
        >
          › Let me answer some questions about this product's value chain
        </HowToHelpOption>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('PRODUCT_WEIRD_IMPACT');
          }}
        >
          › I want to comment on the impact profile
        </HowToHelpOption>
        <HowToHelpOption
          onClick={() => {
            this.props.selectContributionModalType('NEW_PRODUCT');
          }}
        >
          › I cannot find the right product, let me add a new one
        </HowToHelpOption>
        <CancelText onClick={closeModal}>Nevermind, I'll continue exploring</CancelText>
      </>
    );
  }

  public render() {
    const { serverError, currentlyOpenModal, isOpen, user } = this.props;

    if (!isOpen) {
      return null;
    }

    const type =
      currentlyOpenModal && (currentlyOpenModal.options.type as ContributionType | ContributionSelectionType);
    const isComplete = currentlyOpenModal && currentlyOpenModal.options.contributionSubmitComplete;
    const target = currentlyOpenModal && currentlyOpenModal.options.target;
    const targetName = currentlyOpenModal && currentlyOpenModal.options.targetName;

    if (!type) {
      console.error('Missing type for contribution modal');
      return null;
    }

    if (!user) {
      // Allow feedback from anonymous users
      if (type === 'OPEN_COMMENT') {
        if (isComplete) {
          return (
            <Modal modalName={CONTRIBUTE_MODAL_NAME}>
              {({ closeModal }) => (
                <>
                  <ThanksRoot>
                    <ThanksText>
                      <ContributionModalTitle>
                        We've received your message, thanks for reaching out!
                      </ContributionModalTitle>
                    </ThanksText>
                    <ThanksImage src={thanksImage} />
                  </ThanksRoot>
                  <FormRow style={{ justifyContent: 'center' }}>
                    <FullWidthFormButtonWithCenter onClick={closeModal}>
                      Back to exploring
                    </FullWidthFormButtonWithCenter>
                  </FormRow>
                </>
              )}
            </Modal>
          );
        }

        return (
          <Modal modalName={CONTRIBUTE_MODAL_NAME}>
            {() => (
              <>
                {this.getForm('OPEN_COMMENT')}
                {serverError && <ErrorMessage>Error: {serverError}</ErrorMessage>}
              </>
            )}
          </Modal>
        );
      }

      return null;
    }

    if (
      !user.verified &&
      // Give the user an hour to confirm their address
      differenceInMinutes(new Date(), user.createdAt) > 60
    ) {
      return (
        <Modal modalName={CONTRIBUTE_MODAL_NAME}>
          {({ closeModal }) => (
            <>
              <ContributionModalTitle>One more step…</ContributionModalTitle>
              <ContributionModalParagraph>
                We need to confirm that you're actually who you say you are. You should have received an email to{' '}
                <strong>{user.email}</strong> with a confirmation link. Once you've opened that link, you can help
                improve {aiName}.
              </ContributionModalParagraph>
              <FormRow style={{ justifyContent: 'center' }}>
                <FormButton onClick={closeModal}>Close</FormButton>
              </FormRow>
            </>
          )}
        </Modal>
      );
    }

    if (isContributionSelectionType(type)) {
      return (
        <Modal modalName={CONTRIBUTE_MODAL_NAME}>
          {({ closeModal }) =>
            type === 'company'
              ? this.getCompanyContributionTypeSelection(closeModal, targetName)
              : this.getProductContributionTypeSelection(closeModal, targetName)
          }
        </Modal>
      );
    }

    if (isComplete) {
      switch (currentlyOpenModal.options.type) {
        case 'OPEN_COMMENT':
          return (
            <Modal modalName={CONTRIBUTE_MODAL_NAME}>
              {({ closeModal }) => (
                <>
                  {}
                  <ThanksRoot>
                    <ThanksText>
                      <ContributionModalTitle>
                        We've received your message, thanks for reaching out!
                      </ContributionModalTitle>
                    </ThanksText>
                  </ThanksRoot>

                  <FormRow>
                    <NiceButton onClick={closeModal}>Back to exploring</NiceButton>
                  </FormRow>
                </>
              )}
            </Modal>
          );
        default:
          return (
            <Modal modalName={CONTRIBUTE_MODAL_NAME}>
              {({ closeModal }) => (
                <>
                  {}
                  <ThanksRoot>
                    <ThanksText>
                      <ContributionModalTitle>
                        Thanks for sharing your wisdom, {user.firstName || user.username}!
                      </ContributionModalTitle>
                    </ThanksText>
                  </ThanksRoot>

                  <FormRow>
                    <NiceButton onClick={closeModal}>Back to exploring</NiceButton>
                  </FormRow>
                </>
              )}
            </Modal>
          );
      }
    }

    return (
      <Modal modalName={CONTRIBUTE_MODAL_NAME}>
        {() => (
          <>
            {this.getBackLink(type, target)}
            {this.getForm(type)}
            {serverError && <ErrorMessage>Error: {serverError}</ErrorMessage>}
          </>
        )}
      </Modal>
    );
  }
}

const REQUEST_ID = 'REQUEST/CONTRIBUTION';

// eslint-disable-next-line @typescript-eslint/ban-types
export const ContributeModal = connect<StateProps, DispatchProps, {}, StateTree>(
  state => {
    const currentlyOpenModal = getOpenModal(state);
    return {
      isSubmitting: isRequestOngoing(state, REQUEST_ID),
      serverError: getRequestError(state, REQUEST_ID),
      currentlyOpenModal,
      isOpen: !!currentlyOpenModal && currentlyOpenModal.name === CONTRIBUTE_MODAL_NAME,
      user: loggedInUser(state),
    };
  },
  dispatch => ({
    submitContribution: options => {
      dispatch(sendContribution(options, REQUEST_ID));
    },
    selectContributionModalType: type => {
      dispatch(updateModal({ type }));
    },
  }),
)(ContributeModalPlain);
